import {Component, input, computed, signal, output, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MultiSelectModule} from 'primeng/multiselect';
import {Panel} from 'primeng/panel';
import {TranslocoPipe} from '@jsverse/transloco';
import {HrSystemRecordingCardComponent} from '../../../shared/components/hr-system-recording-card/hr-system-recording-card.component';
import {ModuleFunction, DirectusTranslatedItem, NonHrSystems} from '@frontend/directus';
import {Message} from 'primeng/message';
import {ModuleIntegrationConfigComponent} from '../module-integration-config/module-integration-config.component';
import {
  IntegrationConfigOutput,
  ModuleIntegrationConfigVM,
} from '../module-integration-config/module-integration-config.types';
import {Divider} from 'primeng/divider';
import {NonHrSystemRecordingCardComponent} from '../../../shared/components/non-hr-system-recording-card/non-hr-system-recording-card.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {ToastService} from '@frontend/toast';
import {
  ModuleHrIntegrationSystemRecording,
  ModuleNonHrIntegrationSystemRecording,
  ModuleIntegrationConfig,
  FlowDirection,
  ModuleNonHrIntegrationConfig,
  UpsertIntegrationConfigurationsRequestParams,
  SystemRecordingIntegrationNonHrConfigUpsertRequest,
  SystemRecordingIntegrationHrUpsertRequest,
  SystemRecordingIntegrationUpsertControllerService,
} from '@frontend/shared';
import {ProjectStepStateService} from '../../../../../project-step-state.service';

@Component({
  selector: 'app-module-integration-panel',
  imports: [
    CommonModule,
    FormsModule,
    MultiSelectModule,
    Panel,
    TranslocoPipe,
    HrSystemRecordingCardComponent,
    Message,
    ModuleIntegrationConfigComponent,
    Divider,
    NonHrSystemRecordingCardComponent,
    ProjectStepSaveButtonComponent,
  ],
  templateUrl: './module-integration-panel.component.html',
})
export class ModuleIntegrationPanelComponent {
  protected readonly stepState = inject(ProjectStepStateService);
  private readonly toast = inject(ToastService);
  private readonly systemRecordingIntegrationUpsertController = inject(
    SystemRecordingIntegrationUpsertControllerService,
  );

  readonly projectId = input.required<number>();
  readonly isReadOnly = input<boolean>(false);
  moduleFunctions = input.required<ModuleFunction[]>();
  moduleNonHrFunctions = input.required<NonHrSystems[]>();
  dataDomainOptions = input.required<DirectusTranslatedItem[]>();
  integrationTypeOptions = input.required<DirectusTranslatedItem[]>();
  isFirst = input.required<boolean>();
  officeId = input.required<number>();
  officeName = input.required<string>();
  moduleHrIntegrationSystemRecordings = input.required<ModuleHrIntegrationSystemRecording[]>();
  moduleNonHrIntegrationSystemRecordings =
    input.required<ModuleNonHrIntegrationSystemRecording[]>();
  readonly isSaving = input(false);

  save = output<IntegrationConfigOutput[]>();

  private readonly changesHrMap = signal<Map<string, IntegrationConfigOutput>>(new Map());
  private readonly changesNonHrMap = signal<Map<string, IntegrationConfigOutput>>(new Map());

  protected readonly newSystemCardData = computed(
    () => this.moduleHrIntegrationSystemRecordings()[0] ?? null,
  );

  protected readonly integrationSystemsModuleSystemRecording = computed(
    () => this.moduleHrIntegrationSystemRecordings().slice(1) ?? [],
  );

  protected readonly hrIntegrationRows = computed(() =>
    this.integrationSystemsModuleSystemRecording().map((recording) => ({
      recording,
      inboundInputObject: this.buildInputObject(
        recording.inboundIntegrationConfig,
        FlowDirection.INBOUND,
      ),
      outboundInputObject: this.buildInputObject(
        recording.outboundIntegrationConfig,
        FlowDirection.OUTBOUND,
      ),
    })),
  );

  protected readonly nonHrIntegrationRows = computed(() =>
    this.moduleNonHrIntegrationSystemRecordings().map((recording) => ({
      recording,
      inboundInputObject: this.buildInputObject(
        recording.inboundIntegrationConfig,
        FlowDirection.INBOUND,
      ),
      outboundInputObject: this.buildInputObject(
        recording.outboundIntegrationConfig,
        FlowDirection.OUTBOUND,
      ),
    })),
  );

  private readonly pendingChanges = computed<IntegrationConfigOutput[]>(() => [
    ...this.changesHrMap().values(),
    ...this.changesNonHrMap().values(),
  ]);

  readonly hasChanges = computed<boolean>(() => this.pendingChanges().length > 0);

  readonly isValid = computed<boolean>(() => this.pendingChanges().every((item) => item.isValid));

  protected readonly showsSaveButton = computed<boolean>(() => this.hasChanges() && this.isValid());

  protected addHrIntegrationConfigChangeToSaveMap($event: IntegrationConfigOutput) {
    this.changesHrMap.update((map) => {
      map.set($event.temporaryIdentifier, $event);
      return new Map(map);
    });
  }

  protected addNonHrIntegrationConfigChangeToSaveMap($event: IntegrationConfigOutput) {
    this.changesNonHrMap.update((map) => {
      map.set($event.temporaryIdentifier, $event);
      return new Map(map);
    });
  }

  private buildInputObject(
    integrationConfig: ModuleIntegrationConfig | ModuleNonHrIntegrationConfig,
    flowDirection: FlowDirection,
  ): ModuleIntegrationConfigVM {
    const systemRecordingPks =
      'systemRecordingIds' in integrationConfig
        ? integrationConfig.systemRecordingIds
        : integrationConfig.systemRecordingNonHrIds;

    return {
      flowDirection,
      temporaryIdentifier: integrationConfig.temporaryIdentifier,
      systemRecordingPks,
      integrationRequired: integrationConfig.integrationRequired,
      dataDomainDirectusUuids: integrationConfig.dataDomainDirectusUuids,
      integrationTypeUuid: integrationConfig.integrationTypeUuid,
    };
  }

  protected onSubmit(): void {
    const request = this.buildRequest();

    this.systemRecordingIntegrationUpsertController
      .upsertIntegrationConfigurations(request)
      .subscribe({
        next: async () => {
          void this.toast.showSuccessMessage();
          this.changesHrMap.set(new Map());
          this.changesNonHrMap.set(new Map());
          void this.stepState.loadStepStatuses(this.projectId()!);
        },
        error: () => this.toast.showError('common.error', 'error.generic.detail'),
      });
  }

  private buildRequest(): UpsertIntegrationConfigurationsRequestParams {
    const hrIntegrationConfigs = this.buildHrIntegrationConfigsRequest();
    const nonHrIntegrationConfigs = this.buildNonHrIntegrationConfigsRequest();

    return {
      projectId: this.projectId(),
      systemRecordingIntegrationContainerUpsertRequest: {
        officeId: this.officeId(),
        hrIntegrationConfigs: hrIntegrationConfigs,
        nonHrIntegrationConfigs: nonHrIntegrationConfigs,
      },
    };
  }

  private buildNonHrIntegrationConfigsRequest() {
    const changesNonHrMap = this.changesNonHrMap();
    const nonHrIntegrationConfigs: SystemRecordingIntegrationNonHrConfigUpsertRequest[] = [];

    for (const row of changesNonHrMap.values()) {
      if (!row.isValid) {
        continue;
      }

      nonHrIntegrationConfigs.push({
        flowDirection: row.flowDirection,
        systemRecordingNonHrIds: row.systemRecordingPks,
        integrationRequired: row.data.yesNoSelection,
        dataDomainDirectusUuids: row.data.dataDomainDirectusUuids,
        integrationTypeDirectusUuid: row.data.integrationTypeId,
      });
    }
    return nonHrIntegrationConfigs;
  }

  private buildHrIntegrationConfigsRequest() {
    const changesHrMap = this.changesHrMap();

    const hrIntegrationConfigs: SystemRecordingIntegrationHrUpsertRequest[] = [];

    for (const row of changesHrMap.values()) {
      if (!row.isValid) {
        continue;
      }

      hrIntegrationConfigs.push({
        flowDirection: row.flowDirection,
        systemRecordingIds: row.systemRecordingPks,
        integrationRequired: row.data.yesNoSelection,
        dataDomainDirectusUuids: row.data.dataDomainDirectusUuids,
        integrationTypeDirectusUuid: row.data.integrationTypeId,
      });
    }
    return hrIntegrationConfigs;
  }
}
